import { LOCK_MENU_ITEMS, SET_USER, UNLOCK_MENU_ITEMS } from '@root/constants/actionTypes';
import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import io from 'socket.io-client';
import './_lib.scss';
import agent from './agent';
import { PrivateRoute, PublicRoute } from './routes/_routeHOC';
import AppRoutes from './routes/appRoutes';
import { useFileDownloader } from '@root/hooks/useFileDownloader';
import OnBoardingRoutes from './routes/navRoutes/onboardingRoutes';
// import orgOwnerRegistration from '@root/containers/auth/orgOwnerRegistration';

const IndividualCompliance = React.lazy(() => import('@root/containers/compliance/components/complianceChip/index'));
const Login = React.lazy(() => import('@root/pages/auth/Login'));
const LoginNew = React.lazy(() => import('@root/pages/auth/LoginNew'));
const GcpRegistration = React.lazy(() => import('@root/pages/gcp-registration'));
const AwsRegistration = React.lazy(() => import('@root/pages/aws-registration'));
const Logout = React.lazy(() => import('@root/pages/auth/Logout'));
const ForgotPassword = React.lazy(() => import('@root/pages/auth/Forgot-Password'));
const RegistrationForm = React.lazy(() => import('@root/pages/auth/Registration'));
const orgOwnerRegistration = React.lazy(() => import('@root/pages/auth/orgOwnerRegistration'));
import { fetchuserPolicyTableData } from '@containers/dashboard/action';
import RemediationHandler from './RemediationHandler';
const AwsRegistrationError = React.lazy(() => import('@root/pages/error-aws-handle'));
import { Toaster } from '@components/ui/sonner/index';
import AppLogoLoader from './components/AppLogoLoader';
import TextSelectionViperAI from './components/TextSelectionViperAI';
import ChatBotWrapper from './components/ChatBot/ChatBotWrapper';
import GlobalCopyTracker from './components/GlobalCopyTracker';
import { ChatBotProvider } from './context/ChatBotContext';
import './styles/index.scss';
import { removeNotificationBanner, updateNotificationBanner } from './components/notification/actions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import usePubSub from './hooks/usePubSub';
import useEnhancedQuery from './query/useEnhancedQuery';
import { axiosHelper } from '@root/services/axios';
import GlobalScanFetcher from './components/GlobalScanFetcher';

const downloadMapping = value => {
  if (value?.includes('admin_report')) return 'admin_report';
  if (value?.includes('checkmarx_sca_reports')) return 'checkmarx_sca_reports';
  if (value?.includes('checkmarx_dast_reports')) return 'checkmarx_dast_reports';
  if (value?.includes('workload_vulnerabilities_reports')) return 'vulnerability_export';
  if (value?.includes('kubernetes_inventory_reports')) return 'kubernetes_inventory_list';
  if (value?.includes('inventory_reports')) return 'inventory_resource_list';
  if (value?.includes('kubernetes_identity_reports')) return 'kubernetes_identity_list';
  if (value?.includes('kubernetes_compliances_reports')) return 'kubernetes_compliance_list';
  if (value?.includes('resources_vulnerabilities_reports')) return 'resources_vulnerabilities_reports';
  if (value?.includes('kubernetes_vulnerability_reports')) return 'kubernetes_vulnerability_reports';
  if (value?.includes('unified_vulnerability_reports')) return 'vulnerability_inventory_list';
  return '';
};

const mapStateToProps = state => {
  return {
    user: state.user,
    isLoggedIn: state.user.isLoggedIn,
    isMenuLocked: state.onboarding.isMenuLocked,
  };
};

const mapDispatchToProps = dispatch => ({
  setUser: payload => dispatch({ type: SET_USER, payload }),
  lockMenu: () => dispatch({ type: LOCK_MENU_ITEMS }),
  unlockMenu: () => dispatch({ type: UNLOCK_MENU_ITEMS }),
  fetchUserPolicyTable: () => dispatch(fetchuserPolicyTableData),
});

const App = props => {
  const dispatch = useDispatch();
  const { user, isMenuLocked } = props; // details about user
  const { setUser, lockMenu, unlockMenu } = props; // redux hooks
  const [isAppLoading, setAppLoading] = React.useState(true);
  // Session check: use stored user data to determine if authenticated
  // Tokens are now stored in HTTP-only cookies, not accessible via JavaScript
  const storedUser = window.localStorage.getItem('User');
  const isAuthenticated = !!storedUser;
  const { downloadFile } = useFileDownloader();
  // const subtenantID = window.localStorage.getItem('tenantID');
  const publicRoutes = useMemo(
    () => [
      '/login',
      '/logout',
      '/forgot-password',
      '/set-password',
      '/user_invite',
      '/org_user_invite',
      '/gcp/register',
      '/aws/register',
      '/aws',
    ],
    [],
  );
  const { publish } = usePubSub(
    'app',
    useCallback(() => {}, []),
  );
  const queryClient = useQueryClient();
  const socketRef = React.useRef<any>(null);
  const userRef = React.useRef<any>(null);

  React.useEffect(() => {
    (async function () {
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

      if (isPublicRoute) {
        setAppLoading(false);
        return;
      }

      const currentStoredUser = window.localStorage.getItem('User');

      if (!currentStoredUser) {
        try {
          const userRes = await agent.Auth.current();

          if (userRes && !userRes.error && userRes.user) {
            window.localStorage.setItem('User', JSON.stringify(userRes));
            setUser(userRes);
            userRef.current = userRes;
          } else {
            setAppLoading(false);
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return;
          }
        } catch (err) {
          setAppLoading(false);
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
      } else {
        try {
          const userData = JSON.parse(currentStoredUser);
          setUser(userData);
          userRef.current = userData;
        } catch (err) {
          window.localStorage.removeItem('User');
          setAppLoading(false);
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
      }
      setAppLoading(false);
    })();
  }, [publicRoutes, setUser]);

  // Effect to handle socket cleanup when authentication state changes
  useEffect(() => {
    return () => {
      const currentSocket = socketRef.current;
      if (currentSocket) {
        currentSocket.close();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated]);

  // Fetch subtenant data on app load to ensure it's available for export functionality
  const { data: subtenantData } = useQuery(
    ['user_subtenant_list'],
    () => {
      return axiosHelper.axiosGet('user_subtenant_list', true);
    },
    {
      enabled: isAuthenticated && !!user?.info?.user,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
      refetchOnReconnect: false,
    },
  );

  // Store subtenant data in localStorage when available
  useEffect(() => {
    if (subtenantData?.data) {
      localStorage.setItem('subtenant', JSON.stringify(subtenantData.data));

      // Store tenant ID in localStorage for easy access
      const currentTenantName = localStorage.getItem('tenantName');
      if (currentTenantName) {
        const currentTenant = subtenantData.data.find(tenant => tenant.tenant_name === currentTenantName);
        if (currentTenant?.id) {
          localStorage.setItem('tenantID', currentTenant.id);
        }
      } else if (subtenantData.data.length > 0) {
        // If no tenant name is set, use the first tenant's ID
        localStorage.setItem('tenantID', subtenantData.data[0].id);
      }
    }
  }, [subtenantData, user?.info?.user]);

  const handleExportData = useCallback(
    async exportData => {
      try {
        const response = await axiosHelper.axiosGet(
          `code_security/checkmarx/checkmarx-download-export?export_id=${exportData?.data?.export_id}`,
          true,
          null,
          { responseType: 'blob' },
        );
        downloadFile(response, `checkmarx_report_${exportData?.data?.export_id}`);
      } catch (err) {
        console.error('Download API failed:', err);
      }
    },
    [downloadFile],
  );

  useEffect(() => {
    // Only create socket if user is authenticated and subtenant data is available
    if (!isAuthenticated || !subtenantData?.data) {
      return;
    }

    // Use uniqueUserId for WebSocket channel since tokens are in HTTP-only cookies
    // const uniqueUserId = localStorage.getItem('uniqueUserId');
    const tenantID = localStorage.getItem('tenantID');
    const newSocket = io(`${import.meta.env.VITE_WEBSOCKET_NOTIFICATION}?channel=${tenantID}`, {
      transports: ['websocket'], // Use WebSocket transport only to avoid long-polling
    });
    
    newSocket.on('connect', () => {
      // Socket connected
    });
    newSocket.on('alert', async msg => {
      if (msg.channel === tenantID) {
        const parsedMsg = msg.message;
        console.log(parsedMsg, 'asdfasdfasdfasdf');
        const uniqueUserId = localStorage.getItem('uniqueUserId');
        if (parsedMsg?.data?.signed_url && parsedMsg?.data?.unique_id === uniqueUserId) {
          const s3BucketUrl = parsedMsg?.data?.signed_url;
          window.open(s3BucketUrl, '_self');
          removeNotificationBanner(dispatch, {
            status: 'inProgress',
            id: `${downloadMapping(s3BucketUrl)}_download`,
          });
          updateNotificationBanner(dispatch, {
            status: 'success',
            msg: 'Download Completed',
            id: `${downloadMapping(s3BucketUrl)}_download`,
          });
        }
        if (
          parsedMsg?.data?.status === 'Completed' &&
          (parsedMsg?.data?.export_type === 'scan_report' ||
            parsedMsg?.data?.export_type === 'sbom' ||
            parsedMsg?.data?.export_type === 'dast') &&
          parsedMsg?.data?.unique_id === uniqueUserId
        ) {
          removeNotificationBanner(dispatch, {
            status: 'inProgress',
            id:
              parsedMsg?.data?.export_type === 'dast'
                ? `checkmarx_dast_reports_download`
                : `checkmarx_sca_reports_download`,
          });
          updateNotificationBanner(dispatch, {
            status: 'success',
            msg: 'Download Completed',
            id:
              parsedMsg?.data?.export_type === 'dast'
                ? `checkmarx_dast_reports_download`
                : `checkmarx_sca_reports_download`,
          });
          await handleExportData(parsedMsg);
        }
        if (parsedMsg && parsedMsg['event_type'] === 'RESOURCE_TAG') {
          publish('alerts', parsedMsg);
        } else if (parsedMsg && parsedMsg['event_type'] === 'alerts') {
          publish('notification', parsedMsg);
        } else if (parsedMsg && parsedMsg['event_type'] === 'SCAN_HISTORY') {
          publish('scan_history', parsedMsg);
        } else if (parsedMsg && parsedMsg['event_type'] === 'ONE_CLICK_REMEDIATION') {
          publish('one_click_remediation', parsedMsg);

          if (parsedMsg && parsedMsg?.completed !== parsedMsg?.total) {
            updateNotificationBanner(dispatch, {
              status: 'inProgress',
              msg: `Remediation in progress for ${parsedMsg?.completed}/${parsedMsg?.total} resources`,
              id: 'bulk_remediation_rules',
            });
          } else if (parsedMsg && parsedMsg?.completed === parsedMsg?.total) {
            if (parsedMsg?.success === 0) {
              updateNotificationBanner(dispatch, {
                status: 'error',
                htmlMsg: (
                  <span>
                    Remediation failed for {parsedMsg?.completed}/{parsedMsg?.total} Resource(s).
                    <a
                      style={{ textDecoration: 'none', color: 'blue', cursor: 'pointer' }}
                      href={'/remediation-report'}>
                      Click
                    </a>{' '}
                    to see the report{' '}
                  </span>
                ),
                id: 'bulk_remediation_rules',
              });
            } else {
              updateNotificationBanner(dispatch, {
                status: 'success',
                htmlMsg: (
                  <span>
                    Remediation completed successfully for {parsedMsg?.success}/{parsedMsg?.total} Resource(s).
                    <a
                      style={{ textDecoration: 'none', color: 'blue', cursor: 'pointer' }}
                      href={'/remediation-report'}>
                      Click
                    </a>{' '}
                    to see the report{' '}
                  </span>
                ),
                id: 'bulk_remediation_rules',
              });
            }
          }
        } else if (parsedMsg && parsedMsg['event_type'] === 'Repository Scan') {
          queryClient.invalidateQueries(['get_list_repo']);
          if (parsedMsg && parsedMsg?.status === 'inprogress') {
            updateNotificationBanner(dispatch, {
              status: 'inProgress',
              msg: `Repository Scan in progress`,
              id: 'repository_scan',
            });
            // Invalidate the repository list query to trigger a refetch
          } else if (parsedMsg && parsedMsg?.status === 'completed') {
            updateNotificationBanner(dispatch, {
              status: 'success',
              msg: `Repository Scan completed`,
              id: 'repository_scan',
            });
          } else if (parsedMsg && parsedMsg?.status === 'Schedule') {
            updateNotificationBanner(dispatch, {
              status: 'inProgress',
              msg: `Starting Repository Scan...`,
              id: 'repository_scan',
            });
          }
        } else if (parsedMsg && parsedMsg['event_type'] === 'Repository_insert') {
          publish('Repository_insert', parsedMsg);
        } else if (
          parsedMsg &&
          parsedMsg['event_type'] === 'Runtime_Policy_Create' &&
          parsedMsg.unique_id === uniqueUserId
        ) {
          removeNotificationBanner(dispatch, {
            status: 'inProgress',
            id: 'kubernetes_enforcement_policy',
          });
          updateNotificationBanner(dispatch, {
            status: 'success',
            msg: 'Policy Created Successfully',
            id: 'kubernetes_enforcement_policy',
          });
          queryClient.invalidateQueries(['list_enforcement_policies']);
        } else if (
          parsedMsg &&
          parsedMsg['event_type'] === 'Runtime_Policy_Update' &&
          parsedMsg.unique_id === uniqueUserId
        ) {
          removeNotificationBanner(dispatch, {
            status: 'inProgress',
            id: 'kubernetes_enforcement_policy',
          });
          updateNotificationBanner(dispatch, {
            status: 'success',
            msg: 'Policy Updated Successfully',
            id: 'kubernetes_enforcement_policy',
          });
          queryClient.invalidateQueries(['list_enforcement_policies']);
        } else if (
          parsedMsg &&
          parsedMsg['event_type'] === 'Runtime_Policy_Delete' &&
          parsedMsg.unique_id === uniqueUserId
        ) {
          removeNotificationBanner(dispatch, {
            status: 'inProgress',
            id: 'kubernetes_enforcement_policy',
          });
          updateNotificationBanner(dispatch, {
            status: 'success',
            msg: 'Policy Deleted Successfully',
            id: 'kubernetes_enforcement_policy',
          });
          queryClient.invalidateQueries(['list_enforcement_policies']);
        }
        if (parsedMsg && parsedMsg['event_type'] === 'Download Report') {
          publish('download_Report', parsedMsg);
        }
      }
    });
    newSocket.on('message', msg => {
      if (msg.data.channel === tenantID) {
        const parsedMsg = msg.data;
        const uniqueUserId = localStorage.getItem('uniqueUserId');
        if (
          parsedMsg &&
          parsedMsg['event_type'] === 'SUPPRESS_EVENT' &&
          parsedMsg?.id.split('_').pop() === uniqueUserId
        ) {
          // publish('one_click_remediation', parsedMsg);

          if (parsedMsg && parsedMsg?.completed !== parsedMsg?.total) {
            updateNotificationBanner(dispatch, {
              status: 'inProgress',
              msg: `Suppression in progress for ${parsedMsg?.completed}/${parsedMsg?.total} events`,
              id: 'edit_env_suppress_events',
            });
          } else if (parsedMsg && parsedMsg?.completed === parsedMsg?.total) {
            if (parsedMsg?.success === 0) {
              updateNotificationBanner(dispatch, {
                status: 'error',
                htmlMsg: (
                  <span>
                    Event Suppression failed for {parsedMsg?.completed}/{parsedMsg?.total} Event(s).
                  </span>
                ),
                id: 'edit_env_suppress_events',
              });
            } else {
              updateNotificationBanner(dispatch, {
                status: 'success',
                htmlMsg: (
                  <span>
                    Event Suppression completed successfully for {parsedMsg?.success}/{parsedMsg?.total} Event(s).
                  </span>
                ),
                id: 'edit_env_suppress_events',
              });
            }
          }
        }
        if (
          parsedMsg &&
          parsedMsg['event_type'] === 'EVENT_ACTION_UPDATE' &&
          parsedMsg?.id.split('_').pop() === uniqueUserId
        ) {
          if (parsedMsg && parsedMsg?.status === 'inprogress') {
            updateNotificationBanner(dispatch, {
              status: 'inProgress',
              msg: `Threat suppression is in progress for ${parsedMsg?.initiated}/${parsedMsg?.total} events`,
              id: 'event_action_bulk_update',
            });
          } else if (parsedMsg && parsedMsg?.completed === parsedMsg?.total) {
            if (parsedMsg?.success === 0) {
              updateNotificationBanner(dispatch, {
                status: 'error',
                htmlMsg: (
                  <span>
                    Threat suppression is failed for {parsedMsg?.completed}/{parsedMsg?.total} Event(s).
                  </span>
                ),
                id: 'event_action_bulk_update',
              });
            } else {
              updateNotificationBanner(dispatch, {
                status: 'success',
                htmlMsg: (
                  <span>
                    Threat suppression is completed successfully for {parsedMsg?.completed}/{parsedMsg?.total} Event(s).
                  </span>
                ),
                id: 'event_action_bulk_update',
              });
            }
          }
        }
      }
    });
    newSocket.on('disconnect', reason => {
      if (reason === 'io server disconnect') {
        // The disconnection was initiated by the server, you need to reconnect manually
        newSocket.connect();
      }
    });
    newSocket.on('connect_error', error => {
      console.error('Connection error:', error);
    });

    socketRef.current = newSocket; // Store in ref for cleanup

    return () => {
      newSocket.close();
    };
  }, [isAuthenticated, subtenantData, dispatch, publish, queryClient, handleExportData]); // Include all dependencies to satisfy linter

  const { data: configData, isFetching: isFetchingProjectDetails } = useEnhancedQuery(
    ['get_config'],
    () => agent.Tenant_Config.get_config(),
    {
      enabled: isAuthenticated,
    },
  );

  // Check if global filter data is empty to determine onboarding status
  const { data: globalFilterData, isFetching: isFetchingGlobalFilter } = useQuery(
    ['fetchglobalfilter'],
    () => {
      return agent.Environment.fetch_environments_and_cloud_providers();
    },
    {
      enabled: isAuthenticated && !!user?.info, //  Only depends on authenticated state and user
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
      refetchOnReconnect: false,
    },
  );
  // Memoize the global filter data check to prevent unnecessary re-renders
  const hasGlobalFilterData = useMemo(() => {
    return globalFilterData?.data?.length > 0;
  }, [globalFilterData?.data?.length]);

  // Consolidated effect to handle menu locking/unlocking based on user status and global filter data
  useEffect(() => {
    if (!user?.info?.user) return;

    const { account_added, is_super } = user.info.user;

    // Handle super user without account
    if (is_super && !account_added) {
      lockMenu();
      return;
    }

    // Wait for global filter data to load before making decisions
    if (isFetchingGlobalFilter) return;

    // Handle regular users based on global filter data
    if (account_added && !hasGlobalFilterData) {
      lockMenu();
    } else if (account_added && hasGlobalFilterData) {
      unlockMenu();
    }
  }, [user?.info?.user, hasGlobalFilterData, isFetchingGlobalFilter, lockMenu, unlockMenu]);

  useEffect(() => {
    if (!isFetchingProjectDetails) localStorage.setItem('configdata', JSON.stringify(configData));
  }, [configData, isFetchingProjectDetails]);
  // const _appRoutes=  useMemo(() => {
  //   if (isMenuLocked) return <OnBoardingRoutes />;
  //   return (
  //     // <WebSocketProvider>
  //     <AppRoutes />
  //     // </WebSocketProvider>
  //   );
  // ,[])

  return (
    <ChatBotProvider>
      {/* {isAppLoading && <AppLogoLoader />} */}
      {!isAppLoading && (
        <Suspense fallback={<AppLogoLoader />}>
          <BrowserRouter>
            <Switch>
              <PublicRoute path="/development" exact component={IndividualCompliance} />
              <PublicRoute path="/login/old" exact component={Login} />
              <PublicRoute path="/login" exact component={LoginNew} />
              <PublicRoute path="/logout" component={Logout} />
              <PublicRoute path="/forgot-password" exact component={ForgotPassword} />
              <PublicRoute path="/set-password" exact component={ForgotPassword} />
              <PublicRoute path="/user_invite/:subtenant_id/:query__int" exact component={RegistrationForm} />
              <PublicRoute path="/org_user_invite/:user_id/:query__int" exact component={orgOwnerRegistration} />
              <Route path="/gcp/register" exact component={GcpRegistration} />
              <Route path="/aws/register" exact component={AwsRegistration} />
              <Route path="/aws" exact component={AwsRegistrationError} />

              <PrivateRoute
                component={() => {
                  if (isMenuLocked)
                    return (
                      <OnBoardingRoutes
                        globalFilterData={globalFilterData}
                        isFetchingGlobalFilter={isFetchingGlobalFilter}
                      />
                    );
                  return (
                    <>
                      <GlobalScanFetcher />
                      <AppRoutes />
                      <TextSelectionViperAI />
                      <ChatBotWrapper />
                      <GlobalCopyTracker />
                    </>
                  );
                }}
              />
            </Switch>
          </BrowserRouter>
        </Suspense>
      )}

      <RemediationHandler />
      <Toaster richColors position="top-center" />
    </ChatBotProvider>
  );
};

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App);
export default ConnectedApp;
